import i18n from 'i18next';

import {
  ExceedGroupsCustomListItem,
  ExceedGroupsCustomMenu,
  getRestrictedAdminUsersAndGroupsSearch,
  SelectOptionWithCode,
} from '@/exceed';

import getAxiosConfig from '../helpers/getAxiosConfig';
import getLetters from '../helpers/getLetters';

export default ({ id: videoId, queryClient, videoPropertiesKey, videoVersionKey }) => {
  const addLetterTriggerString = i18n.t('add-letter-trigger');
  const submitText = i18n.t('save-changes');
  const letterTriggerOptions = [
    { label: i18n.t('enrollment'), value: 'LetterTriggerEnrollment' },
    { label: i18n.t('attended'), value: 'LetterTriggerPassed' },
    { label: i18n.t('dropped'), value: 'LetterTriggerDropped' },
    { label: i18n.t('no-show'), value: 'LetterTriggerNoShow' },
    { label: i18n.t('start-date-reminder'), value: 'LetterTriggerStartDate' },
    { label: i18n.t('end-date-reminder'), value: 'LetterTriggerEndDate' },
  ];

  const getTimingOptions = (model) => {
    switch (model?.type) {
      case 'LetterTriggerEnrollment':
        return [
          { label: i18n.t('on-enrollment'), value: 0 },
          { label: i18n.t('after-enrollment'), value: 2 },
        ];
      case 'LetterTriggerStartDate':
        return [
          { label: i18n.t('before-start'), value: 1 },
          { label: i18n.t('after-start'), value: 2 },
        ];
      case 'LetterTriggerEndDate':
        return [
          { label: i18n.t('before-end'), value: 1 },
          { label: i18n.t('after-end'), value: 2 },
        ];
      default:
        return [
          { label: i18n.t('on-completion'), value: 0 },
          { label: i18n.t('after-completion'), value: 2 },
        ];
    }
  };

  const getLetterTriggerLabel = (trigger) => {
    const letterName = trigger.letter.name;
    const triggerType = letterTriggerOptions.find(({ value }) => value === trigger.type)?.label;
    return i18n.t('letter-trigger-label', {letterName, triggerType});
  };

  const getLetterTriggerModalSchema = (model) => ({
    type: {
      type: 'Select',
      label: i18n.t('trigger-type'),
      options: letterTriggerOptions,
      selectProps: { isFullWidth: true },
    },
    letter: {
      type: 'AutoCompleteSearch',
      label: i18n.t('letter'),
      handleLoadOptions: getLetters,
      isSingleSelect: true,
      selectProps: {
        components: { Option: SelectOptionWithCode },
        isFullWidth: true,
        isMulti: false,
        placeholder: i18n.t('choose-a-letter'),
      },
      validators: [{ type: 'isRequired' }],
    },
    send_to: {
      type: 'Select',
      label: i18n.t('send-to'),
      options: [
        { label: i18n.t('user-only'), value: 'user' },
        { label: i18n.t('manager-only'), value: 'manager' },
        { label: i18n.t('both-user-and-manager'), value: 'both' },
      ],
      selectProps: { isFullWidth: true },
    },
    timing: {
      type: 'Radio',
      label: i18n.t('timing'),
      help: i18n.t('timing-help'),
      options: getTimingOptions(model),
      validators: [{ type: 'isTimingValidBasedOnTrigger' }],
    },
    offset: {
      type: 'Offset',
      label: i18n.t('offset'),
      help: i18n.t('offset-help'),
      conditions: [{ shouldHideField: true, type: 'isTimingNotOffset' }],
      validators: [{ type: 'isOffsetRequired' }, { type: 'isOffsetValidBasedOnTrigger' }],
    },
    reminder_interval: {
      type: 'Select',
      label: i18n.t('repeat'),
      options: [
        { label: i18n.t('repeat-option-none'), value: 0 },
        { label: i18n.t('repeat-option-weekly'), value: 604800 },
        { label: i18n.t('repeat-option-alternate-days'), value: 172800 },
        { label: i18n.t('repeat-option-daily'), value: 86400 },
      ],
      selectProps: { isFullWidth: true },
      conditions: [{ shouldHideField: true, type: 'isTriggerNotStartDate' }],
    },
    copied_addresses: {
      type: 'Text',
      label: i18n.t('additional-cc-email-addresses'),
      help: i18n.t('additional-cc-email-addresses-help'),
    },
    groups: {
      type: 'AutoCompleteSearch',
      label: i18n.t('group-restrictions'),
      CustomSelectedOptionItem: ExceedGroupsCustomListItem,
      handleLoadOptions: (searchValue) => getRestrictedAdminUsersAndGroupsSearch({ searchValue }),
      selectProps: {
        components: { Option: ExceedGroupsCustomMenu },
        placeholder: i18n.t('add-groups'),
      },
    },
  });

  return {
    invitation_email: {
      type: 'Boolean',
      label: i18n.t('calendar-invites'),
      help: i18n.t('calendar-invites-field-description'),
      labelText: i18n.t('enable-calendar-invites'),
    },
    letter_triggers: {
      type: 'NewItemForm',
      label: i18n.t('letter-triggers'),
      help: i18n.t('letter-triggers-help'),
      addButtonText: addLetterTriggerString,
      addModal: {
        getAxiosConfig: (data) => getAxiosConfig('create-letter-trigger', { videoId, ...data }),
        getSchema: getLetterTriggerModalSchema,
        model: {
          groups: [],
          reminder_interval: 0,
          send_to: 'user',
          timing: 0,
          type: 'LetterTriggerEnrollment',
        },
        submitText,
        title: addLetterTriggerString,
      },
      deleteModal: {
        body: i18n.t('delete-letter-trigger-body'),
        getAxiosConfig: (data) => getAxiosConfig('delete-letter-trigger', { videoId, ...data }),
        title: i18n.t('delete-letter-trigger'),
      },
      editModal: {
        getAxiosConfig: (data) => getAxiosConfig('update-letter-trigger', { videoId, ...data }),
        getSchema: getLetterTriggerModalSchema,
        submitText,
        title: i18n.t('edit-letter-trigger'),
      },
      emptyText: i18n.t('letter-triggers-empty'),
      getItemLabel: getLetterTriggerLabel,
      onSubmit: () => {
        queryClient.invalidateQueries(videoPropertiesKey);
        queryClient.invalidateQueries(videoVersionKey);
      },
    },
  };
};
